/**
 * local slice: 내 트랙, 디바이스, 뮤트 상태 관리
 * wehago-conference의 redux/modules/local 에서 리팩토링
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CreatorInfo } from '@/types/api';
import type { LinkedMeeting } from '@/types/components';
import { loadBoolean, loadNumber } from '@/utils/localStorage';
import { toAbsoluteProfileUrl } from '@/utils/profileUtils';

export interface LocalState {
  // 사용자 정보 (API getUserInfo 응답)
  userId: string;       // userInfo.portal_id
  userName: string;     // userInfo.user_name
  wehagoId: string;     // userInfo.portal_id (Jitsi 식별자)
  nickname: string;     // userInfo.nickname
  profileUrl: string;   // userInfo.profile_url
  userNo: string;      // userInfo.user_no (숫자형 사용자 고유번호)
  companyCode: string;
  companyNo: string;    // userInfo.company_no
  employeeNo: string;   // userInfo.employee_no (직원 정보 시퀀스)
  avatarUrl: string;
  selectedAvatar: string;
  nationality: string;

  // 회의 정보
  roomId: string;
  roomName: string;
  token: string;        // mediaServerToken (Jitsi XMPP 연결용)
  isPublic: boolean;
  securityType: 'MASTER_APPROVAL' | 'PASSWORD' | ''; // 비공개방 인증 방식 (API 원본 값)
  userIdentify: string; // lpevent/standby API에서 사용하는 사용자 식별자
  joinCode: string;     // password 타입 비공개방 비밀번호 (방 정보의 join_code)
  conferenceId: string; // 회의 ID (방 정보의 conference_id)
  startDatetime: number | null;
  endDatetime: number | null;
  /** 회의 개설자 정보 (getVideoRoomInfo → creator_info) */
  creatorInfo: CreatorInfo | null;
  /** 연결된 회의 정보 */
  linkedMeeting: LinkedMeeting | null;
  /** 크레딧 소모 방식: 회의 생성자(MASTER) vs 참석자(USER) */
  creditMode: 'MASTER' | 'USER';
  /** AI 번역 설정 — null이면 번역 기능 비활성화 */
  aiTranslationType: 'USE_CREATOR_CREDIT' | 'USE_PARTICIPANT_CREDIT' | null;

  // 트랙 상태 (실제 JitsiLocalTrack 객체는 Redux 밖에서 관리)
  audioMuted: boolean;
  videoMuted: boolean;
  speakerMuted: boolean;
  hasAudioTrack: boolean;
  hasVideoTrack: boolean;
  hasScreenShareTrack: boolean;

  // 노이즈 제거
  noiseSuppressionEnabled: boolean;

  // 디바이스
  audioInputDeviceId: string;
  videoInputDeviceId: string;
  audioOutputDeviceId: string;
  audioInputDevices: MediaDeviceInfo[];
  videoInputDevices: MediaDeviceInfo[];
  audioOutputDevices: MediaDeviceInfo[];

  // Jitsi 참여자 ID (room.myUserId() — 채팅 senderId 비교용)
  jitsiId: string;

  // 역할
  isModerator: boolean;
  isGuest: boolean;       // 비인증 게스트 (getUserInfo 실패 시 true)

  // 비디오 화면 조절
  videoRotate: 0 | 90 | 180 | 270;
  isVideoReverse: boolean;

  // 네트워크 품질 (0~100, 100 = 최상)
  connectionQuality: number;

  // ONE AI 배포 여부
  isAbleOneAI: boolean;

  // 오디오 설정 (localStorage 영속)
  isSpaceUnmute: boolean;   // 스페이스바 음소거 해제 (PTT)
  isMuteOnJoin: boolean;    // 회의 참가 시 마이크 음소거
  speakerVolume: number;    // 스피커 볼륨 (0~100)

  // UI 상태
  isConnected: boolean;
  isConnecting: boolean;
  isReconnecting: boolean;
}

const getCreditModeFromAiTranslationType = (
  aiTranslationType: LocalState['aiTranslationType'],
): LocalState['creditMode'] => (
  aiTranslationType === 'USE_PARTICIPANT_CREDIT' ? 'USER' : 'MASTER'
);

// ── localStorage 영속 키 ────────────────────────────────────────────────────
const STORAGE_KEY_SPACE_UNMUTE = 'wehago_conf_spaceUnmute';
const STORAGE_KEY_MUTE_ON_JOIN = 'wehago_conf_muteOnJoin';
const STORAGE_KEY_SPEAKER_VOLUME = 'wehago_conf_speakerVolume';
const STORAGE_KEY_SELECTED_AVATAR = 'wehago_conf_selectedAvatar';

const initialState: LocalState = {
  userId: '',
  userName: '',
  wehagoId: '',
  nickname: '',
  profileUrl: '',
  userNo: '',
  companyCode: '',
  companyNo: '',
  employeeNo: '',
  avatarUrl: '',
  selectedAvatar: (() => { try { return localStorage.getItem(STORAGE_KEY_SELECTED_AVATAR) || ''; } catch { return ''; } })(),
  nationality: '',

  roomId: '',
  roomName: '',
  token: '',
  isPublic: false,
  securityType: '',
  userIdentify: '',
  joinCode: '',
  conferenceId: '',
  startDatetime: null,
  endDatetime: null,
  creatorInfo: null,
  linkedMeeting: null,
  creditMode: 'MASTER',
  aiTranslationType: null,

  audioMuted: false,
  videoMuted: false,
  speakerMuted: false,
  hasAudioTrack: false,
  hasVideoTrack: false,
  hasScreenShareTrack: false,
  noiseSuppressionEnabled: false,

  audioInputDeviceId: '',
  videoInputDeviceId: '',
  audioOutputDeviceId: '',
  audioInputDevices: [],
  videoInputDevices: [],
  audioOutputDevices: [],

  jitsiId: '',
  isModerator: false,
  isGuest: false,

  connectionQuality: 100,

  isAbleOneAI: false,

  isSpaceUnmute: loadBoolean(STORAGE_KEY_SPACE_UNMUTE, true),
  isMuteOnJoin: loadBoolean(STORAGE_KEY_MUTE_ON_JOIN, false),
  speakerVolume: loadNumber(STORAGE_KEY_SPEAKER_VOLUME, 50, 0, 100),

  isConnected: false,
  isConnecting: false,
  isReconnecting: false,

  videoRotate: 0,
  isVideoReverse: false,
};

const localSlice = createSlice({
  name: 'local',
  initialState,
  reducers: {
    /** getUserInfo() API 응답으로 사용자 정보 설정 */
    setUserInfo(state, action: PayloadAction<{
      portal_id?: string;
      user_name?: string;
      user_no?: string | number;
      nickname?: string;
      profile_url?: string;
      company_no?: string | number;
      employee_no?: string | number;
      // 하위 호환: 직접 지정
      userId?: string;
      userName?: string;
      wehagoId?: string;
      companyCode?: string;
      avatarUrl?: string;
      nationality?: string;
      isGuest?: boolean;
    }>) {
      const p = action.payload;
      // API 응답 필드 우선, 직접 지정 필드 폴백
      const portalId = p.portal_id ?? p.userId ?? p.wehagoId ?? '';
      state.userId = portalId;
      state.wehagoId = portalId;
      state.isGuest = p.isGuest ?? false;
      state.userName = p.user_name ?? p.userName ?? '';
      state.nickname = p.nickname ?? '';
      state.profileUrl = toAbsoluteProfileUrl(p.profile_url) ?? '';
      state.userNo = String(p.user_no ?? '');
      state.companyNo = String(p.company_no ?? '');
      state.employeeNo = String(p.employee_no ?? '');
      state.companyCode = p.companyCode ?? '';
      // profile_url(API 응답)이 있으면 avatarUrl로도 사용
      state.avatarUrl = p.avatarUrl ?? p.profile_url ?? '';
      state.nationality = p.nationality ?? '';
    },

    /** getVideoRoomInfo() + requestRoomToken() 응답으로 방 정보 설정 */
    setRoomInfo(state, action: PayloadAction<{
      roomId: string;
      roomName?: string;
      token?: string;
      isPublic?: boolean;
      securityType?: 'MASTER_APPROVAL' | 'PASSWORD' | '';
      userIdentify?: string;
      joinCode?: string;
      conferenceId?: string;
      startDatetime?: number | null;
      endDatetime?: number | null;
      creatorInfo?: CreatorInfo | null;
      linkedMeeting?: LinkedMeeting | null;
      /** 크레딧 소모 방식: MASTER=회의 생성자, USER=참석자 개별 */
      creditMode?: 'MASTER' | 'USER';
      /** AI 번역 설정 — null이면 번역 기능 비활성화 */
      aiTranslationType?: 'USE_CREATOR_CREDIT' | 'USE_PARTICIPANT_CREDIT' | null;
    }>) {
      state.roomId = action.payload.roomId;
      state.roomName = action.payload.roomName ?? action.payload.roomId;
      state.token = action.payload.token ?? '';
      state.isPublic = action.payload.isPublic ?? false;
      state.securityType = action.payload.securityType ?? '';
      state.userIdentify = action.payload.userIdentify ?? '';
      state.joinCode = action.payload.joinCode ?? '';
      state.conferenceId = action.payload.conferenceId ?? '';
      state.startDatetime = action.payload.startDatetime ?? null;
      state.endDatetime = action.payload.endDatetime ?? null;
      state.creatorInfo = action.payload.creatorInfo ?? null;
      state.linkedMeeting = action.payload.linkedMeeting ?? null;
      state.aiTranslationType = action.payload.aiTranslationType ?? null;
      state.creditMode = action.payload.creditMode ?? getCreditModeFromAiTranslationType(state.aiTranslationType);
    },

    /**
     * 회의방 정보 부분 병합 (회의 설정 변경 후 갱신용).
     * setRoomInfo는 전체 덮어쓰기라 conferenceId/startDatetime/creatorInfo 등이
     * 누락되어 사라지는 문제가 있어 별도 액션으로 분리.
     */
    mergeRoomInfo(state, action: PayloadAction<Partial<{
      token: string;
      roomName: string;
      isPublic: boolean;
      securityType: 'MASTER_APPROVAL' | 'PASSWORD' | '';
      joinCode: string;
      linkedMeeting: LinkedMeeting | null;
      creditMode: 'MASTER' | 'USER';
      aiTranslationType: 'USE_CREATOR_CREDIT' | 'USE_PARTICIPANT_CREDIT' | null;
    }>>) {
      const p = action.payload;
      if (p.token !== undefined) state.token = p.token;
      if (p.roomName !== undefined) state.roomName = p.roomName;
      if (p.isPublic !== undefined) state.isPublic = p.isPublic;
      if (p.securityType !== undefined) state.securityType = p.securityType;
      if (p.joinCode !== undefined) state.joinCode = p.joinCode;
      if (p.linkedMeeting !== undefined) state.linkedMeeting = p.linkedMeeting;
      if (p.aiTranslationType !== undefined) {
        state.aiTranslationType = p.aiTranslationType;
        if (p.creditMode === undefined) {
          state.creditMode = getCreditModeFromAiTranslationType(p.aiTranslationType);
        }
      }
      if (p.creditMode !== undefined) state.creditMode = p.creditMode;
    },

    setAudioMuted(state, action: PayloadAction<boolean>) {
      state.audioMuted = action.payload;
    },

    setVideoMuted(state, action: PayloadAction<boolean>) {
      state.videoMuted = action.payload;
    },

    setSpeakerMuted(state, action: PayloadAction<boolean>) {
      state.speakerMuted = action.payload;
    },

    setHasAudioTrack(state, action: PayloadAction<boolean>) {
      state.hasAudioTrack = action.payload;
    },

    setHasVideoTrack(state, action: PayloadAction<boolean>) {
      state.hasVideoTrack = action.payload;
    },

    setHasScreenShareTrack(state, action: PayloadAction<boolean>) {
      state.hasScreenShareTrack = action.payload;
    },

    setNoiseSuppressionEnabled(state, action: PayloadAction<boolean>) {
      state.noiseSuppressionEnabled = action.payload;
    },

    setAudioInputDeviceId(state, action: PayloadAction<string>) {
      state.audioInputDeviceId = action.payload;
    },

    setVideoInputDeviceId(state, action: PayloadAction<string>) {
      state.videoInputDeviceId = action.payload;
    },

    setAudioOutputDeviceId(state, action: PayloadAction<string>) {
      state.audioOutputDeviceId = action.payload;
    },

    setDevices(state, action: PayloadAction<{
      audioInputDevices?: MediaDeviceInfo[];
      videoInputDevices?: MediaDeviceInfo[];
      audioOutputDevices?: MediaDeviceInfo[];
    }>) {
      if (action.payload.audioInputDevices) {
        state.audioInputDevices = action.payload.audioInputDevices;
      }
      if (action.payload.videoInputDevices) {
        state.videoInputDevices = action.payload.videoInputDevices;
      }
      if (action.payload.audioOutputDevices) {
        state.audioOutputDevices = action.payload.audioOutputDevices;
      }
    },

    setJitsiId(state, action: PayloadAction<string>) {
      state.jitsiId = action.payload;
    },

    setModerator(state, action: PayloadAction<boolean>) {
      state.isModerator = action.payload;
    },

    setConnecting(state, action: PayloadAction<boolean>) {
      state.isConnecting = action.payload;
    },

    setConnected(state, action: PayloadAction<boolean>) {
      state.isConnected = action.payload;
      state.isConnecting = false;
    },

    setReconnecting(state, action: PayloadAction<boolean>) {
      state.isReconnecting = action.payload;
    },

    setConnectionQuality(state, action: PayloadAction<number>) {
      state.connectionQuality = action.payload;
    },

    setIsAbleOneAI(state, action: PayloadAction<boolean>) {
      state.isAbleOneAI = action.payload;
    },

    setVideoRotate(state, action: PayloadAction<0 | 90 | 180 | 270>) {
      state.videoRotate = action.payload;
    },

    toggleVideoReverse(state) {
      state.isVideoReverse = !state.isVideoReverse;
    },

    setSelectedAvatar(state, action: PayloadAction<string>) {
      state.selectedAvatar = action.payload;
      try { localStorage.setItem(STORAGE_KEY_SELECTED_AVATAR, action.payload); } catch { /* */ }
    },

    setSpaceUnmute(state, action: PayloadAction<boolean>) {
      state.isSpaceUnmute = action.payload;
      try { localStorage.setItem(STORAGE_KEY_SPACE_UNMUTE, String(action.payload)); } catch { /* */ }
    },

    setMuteOnJoin(state, action: PayloadAction<boolean>) {
      state.isMuteOnJoin = action.payload;
      try { localStorage.setItem(STORAGE_KEY_MUTE_ON_JOIN, String(action.payload)); } catch { /* */ }
    },

    setSpeakerVolume(state, action: PayloadAction<number>) {
      const vol = Math.max(0, Math.min(100, action.payload));
      state.speakerVolume = vol;
      try { localStorage.setItem(STORAGE_KEY_SPEAKER_VOLUME, String(vol)); } catch { /* */ }
    },

    reset() {
      return initialState;
    },
  },
});

export const localActions = localSlice.actions;
export default localSlice.reducer;
